import {
  CheckCircle,
  Clock,
  FileText,
  InfoIcon,
  MessageSquareIcon,
  Pause,
  Play,
  XCircle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@shared/ui/use-toast";
// Import the new generic components
import { RequestCommunications } from "@features/cx/shared/components/communicationLandingTab";
import { RejectRequestDialogue } from "@features/cx/shared/components/RejectRequestDialogue";
import { RequestHeader } from "@features/cx/shared/components/RequestHeader";
import { RequestSummaryCards } from "@features/cx/shared/components/RequestSummaryCards";
import { RequestQuickActions } from "@features/cx/shared/components/RequestQuickActions";
import { ActivitySidebar } from "@features/cx/shared/components/ActivitySidebar";
import { NotesTab } from "@features/cx/shared/components/tabs/NotesTab";
import { ServiceOrdersTab } from "@features/cx/shared/components/tabs/ServiceOrdersTab";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import { ActionButton } from "@features/cx/shared/types/consumerRequest";
import {
  SERVICE_REQUEST_STATUS,
  SERVICE_REQUEST_STATUS_DISPLAY,
} from "@features/cx/shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Tabs } from "@shared/ui/tabs";
import { useEffect, useState } from "react";
import { useComplaintDetails, useUpdateComplaintStatus } from "../../hooks";
import ComplaintDetailsTab from "./tabComponents/complaintDetailsTab";
import { formatTerritoryAddress } from "@shared/utils/address";
import { useServiceOrders } from "@features/cx/shared/hooks/useServiceOrder";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

interface ClosureRequirements {
  customerConfirmation: boolean;
  qualityAssuranceDone: boolean;
}

const ComplaintDetailPage = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const { id } = useParams<{ id: string }>();
  const { data: complaintRequestData } = useComplaintDetails(Number(id));
  const [closureRequirements, setClosureRequirements] =
    useState<ClosureRequirements>({
      customerConfirmation: false,
      qualityAssuranceDone: false,
    });
    

  const [currentStatus, setCurrentStatus] = useState<
    | "pending"
    | "in progress"
    | "completed"
    | "cancelled"
    | "draft"
    | "submitted"
    | "approved"
    | "rejected"
    | "on hold"
  >("pending");

  // Update currentStatus when API data is loaded
  useEffect(() => {
    if (complaintRequestData?.statusDisplay) {
      const statusFromAPI = complaintRequestData.statusDisplay.toLowerCase();
      setCurrentStatus(
        statusFromAPI as
        | "pending"
        | "in progress"
        | "completed"
        | "cancelled"
        | "draft"
        | "submitted"
        | "approved"
        | "rejected"
        | "on hold",
      );
    }
  }, [complaintRequestData]);

  const { mutateAsync: updateComplaintRequest } = useUpdateComplaintStatus(
    Number(id),
  );

  const [isRejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const navigate = useNavigate();

  const request = complaintRequestData;

  // Status change handlers
  const handleAcknowledge = () => {
    updateComplaintRequest({
      requestId: Number(id),
      payload: {
        status: SERVICE_REQUEST_STATUS.IN_PROGRESS,
      },
    });
    setCurrentStatus(
      SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.IN_PROGRESS],
    );
  };

  const handlePutOnHold = () => {
    updateComplaintRequest({
      requestId: Number(id),
      payload: {
        status: SERVICE_REQUEST_STATUS.ON_HOLD,
      },
    });
    setCurrentStatus(
      SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.ON_HOLD],
    );
  };

  const handleResume = () => {
    updateComplaintRequest({
      requestId: Number(id),
      payload: {
        status: SERVICE_REQUEST_STATUS.IN_PROGRESS,
      },
    });
    setCurrentStatus(
      SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.IN_PROGRESS],
    );
  };

  const handleResolveRequest = () => {
    const { customerConfirmation, qualityAssuranceDone } = closureRequirements;
    const requiresApproval =
      complaintRequestData?.utilitySupportRequest?.extraData?.requiresApproval;
    const allFieldsValid = customerConfirmation && qualityAssuranceDone;
    if (requiresApproval && !allFieldsValid) {
      toast({
        title: "Error",
        description:
          "Please complete all required closure steps before resolving.",
        variant: "destructive",
      });
      return;
    }
    updateComplaintRequest(
      {
        requestId: Number(request?.id),
        payload: {
          status: SERVICE_REQUEST_STATUS.COMPLETED,
          closeDate: new Date().toISOString(),
          additionalData: {
            closureRequirements: {
              customerConfirmation: closureRequirements.customerConfirmation,
              qualityAssuranceDone: closureRequirements.qualityAssuranceDone,
            },
          },
        },
      },
      {
        onSuccess() {
          toast({
            title: "Success",
            description: "Complaint request resolved successfully!",
            variant: "default",
          });
          navigate("/cx/complaints?tab=current&page=1");
        },
      },
    );
    setCurrentStatus(
      SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.COMPLETED],
    );
  };

  const handleRejectRequest = () => {
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async (reason: string) => {
    setIsRejecting(true);
    try {
      await updateComplaintRequest({
        requestId: Number(id),
        payload: {
          status: SERVICE_REQUEST_STATUS.REJECTED,
          closeDate: new Date().toISOString(),
          closureRemark:
            reason || "complaint request rejected by the consumer!",
        },
      });
      setCurrentStatus("rejected");
      setRejectDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reject complaint request.",
        variant: "destructive",
      });
    } finally {
      setIsRejecting(false);
    }
  };

  // Action buttons for RequestHeader
  const actionButtons: ActionButton[] = [
    {
      id: "acknowledge",
      label: "Acknowledge",
      icon: CheckCircle,
      variant: "default",
      onClick: handleAcknowledge,
      condition: (status) =>
        status ===
        SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.PENDING],
    },
    {
      id: "hold",
      label: "Put on Hold",
      icon: Pause,
      variant: "outline",
      onClick: handlePutOnHold,
      condition: (status) =>
        status ===
        SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.IN_PROGRESS],
    },
    {
      id: "resume",
      label: "Resume",
      icon: Play,
      variant: "default",
      onClick: handleResume,
      condition: (status) =>
        status ===
        SERVICE_REQUEST_STATUS_DISPLAY[SERVICE_REQUEST_STATUS.ON_HOLD],
    },
  ];

  // Transform API data to match RequestHeader interface
  const transformedRequestForHeader = {
    id: request?.id?.toString() || "",
    name: request?.utilitySupportRequest?.name || "NA",
    type: request?.utilitySupportRequest?.supportRequestType || "NA",
    subType: request?.utilitySupportRequest?.supportRequestSubtype || "NA",
    reason: request?.requestType || "",
    dateRequested: request?.createdDate || "",
    requestNo: request?.requestNo || "",
    customerName:
      `${request?.consumer.firstName || ""} ${request?.consumer.lastName || ""
        }`.trim() || "Unknown Customer",
    accountNumber: request?.consumer.consumerNo || "",
    priority: request?.utilitySupportRequest?.extraData?.severityLevel || 0,
    status: currentStatus,
    isVip: request?.consumer.isVip || false,
    sla: {
      status: request?.slaSummary?.slaStatus || "",
      dueDate: request?.slaSummary?.slaDueDate || "",
      remaining: request?.slaSummary?.timeRemaining || "",
    },
    priorityDisplay: request?.priorityLevelDisplay,
  };

  // Transform API data to match RequestSummaryCards interface
  const transformedRequestForSummary = {
    id: request?.id?.toString() || "NA",
    dateRequested: request?.createdDate || "NA",
    requestNo: request?.requestNo || "NA",
    requestedBy: request?.createdUserRemoteName || "NA",
    assignedTo: request?.createdUserRemoteName || "Unassigned",
    lastUpdatedOn: request?.createdDate || "NA",
    lastUpdatedBy: request?.createdUserRemoteName || "NA",
    categoryDisplay: request?.categoryDisplay || "NA",
    subCategoryDisplay: request?.subCategoryDisplay || "NA",
    resolvedOn: request?.closeDate || "NA",
    resolvedBy: request?.resolvedBy || "NA",
    customer: {
      consumerNo: request?.consumer.consumerNo || "NA",
      id: request?.consumer.id?.toString() || "NA",
      firstName: request?.consumer.firstName || "NA",
      lastName: request?.consumer.lastName || "NA",
      phone: request?.consumer.contactNumber || "NA",
      email: request?.consumer.email || "NA",
      category: request?.consumer.category || "NA",
      subCategory: request?.consumer.subCategory || "NA",

      serviceAddress:
        formatTerritoryAddress(
          { service: request?.consumer.consumerAddress.service.hierarchyNames },
          "service",
        ) || "No address configured",
      billingAddress:
        formatTerritoryAddress(
          { billing: request?.consumer.consumerAddress.billing.hierarchyNames },
          "billing",
        ) || "No address configured",
      categoryDisplay: request?.categoryDisplay || "NA",
      subCategoryDisplay: request?.subCategoryDisplay || "NA",
      ownershipDisplay:request?.consumer?.ownershipDisplay || "NA",
      masterAccountNo: request?.consumer?.ownershipDetails?.approvalDetails?.masterAccountNo || "NA",
    },
  };

  // Simplified request for QuickActions
  const requestForQuickActions = {
    id: request?.id?.toString() || "",
    requestNo: request?.requestNo || "",
    customer: {
      id: request?.consumer.id?.toString() || "NA",
      firstName: request?.consumer.firstName || "NA",
      lastName: request?.consumer.lastName || "NA",
      phone: request?.consumer.contactNumber || "NA",
      email: request?.consumer.email || "NA",
      consumerNo: request?.consumer.consumerNo || "NA",
    }
  };

  return (
    <div className="space-y-4">
      {/* Main Content Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* Left Column (3/4): Header, Summary, and Tabs */}
        <div className="lg:col-span-3 space-y-6">
          <RequestHeader
            request={transformedRequestForHeader}
            currentStatus={currentStatus.toLowerCase()}
            actionButtons={actionButtons}
            action={"Complaint"}
          >
            <RequestSummaryCards
              request={transformedRequestForSummary}
            />

            {/* Tabs Section */}
            <div className="mt-6 border-t pt-4">
              <Tabs
                className="w-full"
                tabsListClassName="grid w-full grid-cols-5 mb-6"
                defaultValue="details"
                urlMapping={{
                  details: "details",
                  communication: "communication",
                  serviceOrders: "complaint-orders",
                }}
                tabComponents={{
                  details: {
                    label: "Details",
                    icon: <InfoIcon className="h-4 w-4" />,
                    component: (
                      <ComplaintDetailsTab
                        request={request}
                        onResolve={handleResolveRequest}
                        onReject={handleRejectRequest}
                        closureRequirements={closureRequirements}
                        setClosureRequirements={setClosureRequirements}
                      />
                    ),
                  },
                  serviceOrders: {
                    label: "Service Orders",
                    icon: <CheckCircle className="h-4 w-4" />,
                    component: (
                      <ServiceOrdersTab
                        consumerAccountNumber={request?.consumer.consumerNo || ""}
                        consumerId={request?.consumer.id?.toString() || ""}
                        consumerName={
                          request?.consumer.firstName +
                          " " +
                          request?.consumer.lastName
                        }
                        requestId={request?.id?.toString() || ""}
                        remoteUtilityId={Number(remoteUtilityId) || 0}
                        currentStatus={request?.statusDisplay}
                      />
                    ),
                  },
                  communication: {
                    label: "Communication",
                    icon: <MessageSquareIcon className="h-4 w-4" />,
                    component: (
                      <RequestCommunications
                        entityId={request?.id?.toString() || ""}
                        remoteUtilityId={Number(remoteUtilityId)}
                        email={request?.consumer?.email}
                        phone={request?.consumer?.contactNumber || ""}
                        action="consumer_support_request"
                      />
                    ),
                  },
                  notes: {
                    label: "Notes",
                    icon: <FileText className="h-4 w-4" />,
                    component: (
                      <NotesTab
                        source={null}
                        requestId={request?.id?.toString() || ""}
                        remoteUtilityId={Number(remoteUtilityId) || 0}
                      />
                    ),
                  },
                  timeLine: {
                    label: "Time Line",
                    icon: <Clock className="h-4 w-4" />,
                    component: (
                      <TimelineTab
                        entityId={request?.id?.toString() || ""}
                        remoteUtilityId={Number(remoteUtilityId) || 0}
                        actionType="complaint"
                      />
                    ),
                  },
                }}
              />
            </div>
          </RequestHeader>
        </div>

        {/* Right Column (1/4): Quick Actions and Activity Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          <RequestQuickActions 
            request={requestForQuickActions}
            currentStatus={currentStatus}
            actionButtons={actionButtons} 
            handleResolveRequest={handleResolveRequest}
            handleRejectRequest={handleRejectRequest}
          />
          <ActivitySidebar
            entityId={request?.id?.toString() || ""}
            remoteUtilityId={Number(remoteUtilityId) || 0}
            actionType="complaint"
          />
        </div>
      </div>

      <RejectRequestDialogue
        isOpen={isRejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onReject={handleRejectConfirm}
        title="Reject complaint Request"
        description="Please provide a reason for rejecting this complaint request."
        label="Rejection Reason"
        placeholder="Please provide a reason for rejecting this request..."
        rejectButtonText="Reject Request"
        cancelButtonText="Cancel"
        isSubmitting={isRejecting}
        icon={<XCircle className="h-5 w-5 text-red-600" />}
      />
    </div>
  );
};

export default ComplaintDetailPage;
